import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { SupportedLocale, type TextDirection } from '@optiflow/types';
import { directionFor, normalizeLocale } from '@/i18n';

export interface UseLocaleResult {
  locale: SupportedLocale;
  direction: TextDirection;
  isRtl: boolean;
  setLocale: (locale: SupportedLocale) => void;
  toggleLocale: () => void;
}

export function useLocale(): UseLocaleResult {
  const { i18n } = useTranslation();
  const locale = normalizeLocale(i18n.resolvedLanguage ?? i18n.language);

  const setLocale = useCallback(
    (next: SupportedLocale) => {
      // The languageChanged listener in src/i18n updates <html lang> and dir.
      void i18n.changeLanguage(next);
    },
    [i18n],
  );

  const toggleLocale = useCallback(() => {
    setLocale(locale === SupportedLocale.AR ? SupportedLocale.EN : SupportedLocale.AR);
  }, [locale, setLocale]);

  const direction = directionFor(locale);

  return { locale, direction, isRtl: direction === 'rtl', setLocale, toggleLocale };
}
